import { observer } from 'mobx-react-lite'
import { motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { store } from '@/src/stores/store'
import { playButtonSecondarySound } from '@/src/game/audio/uiSounds'
import { getGameShareUrl, shareGameLink } from '@/src/utils/shareGameLink'
import { appendRefIdToUrl } from '@/src/utils/refIdShareParam'
import Button from '../ui/buttons/Button'
import TextHeadline from '../ui/texts/TextHeadline'
import clsx from 'clsx'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconClose from '../ui/icons/IconClose'
import IconOutlink from '../ui/icons/IconOutlink'
import Toggle from '../ui/toggle/Toggle'
import IconInactiveSoundOn from '../ui/icons/settings/IconInactiveSoundOn'
import IconInactiveSoundOff from '../ui/icons/settings/IconInactiveSoundOff'
import IconActiveSoundOff from '../ui/icons/settings/IconActiveSoundOff'
import IconActiveTutorialOn from '../ui/icons/settings/IconActiveTutorialOn'
import IconInactiveTutorialOff from '../ui/icons/settings/IconInactiveTutorialOff'
import IconActiveTutorialOff from '../ui/icons/settings/IconActiveTutorialOff'
import IconInactiveTutorialOn from '../ui/icons/settings/IconInactiveTutorialOn'
import IconActiveVibrationOn from '../ui/icons/settings/IconActiveVibrationOn'
import IconActiveVibrationOff from '../ui/icons/settings/IconActiveVibrationOff'
import IconActiveFullscreenOn from '../ui/icons/settings/IconActiveFullscreenOn'
import IconInactiveFullscreenOff from '../ui/icons/settings/IconInactiveFullscreenOff'
import IconActiveFullscreenOff from '../ui/icons/settings/IconActiveFullscreenOff'
import IconInactiveFullscreenOn from '../ui/icons/settings/IconInactiveFullscreenOn'
import IconInactiveVibrationOff from '../ui/icons/settings/IconInactiveVibrationOff'
import IconInactiveVibrationOn from '../ui/icons/settings/IconInactiveVibrationOn'
import IconActiveSoundOn from '../ui/icons/settings/IconActiveSoundOn'

const backdropVariants = {
    hidden: { opacity: 0, scale: 0.96 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.18,
            ease: 'easeOut' as const,
            when: 'beforeChildren' as const,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.98,
        transition: {
            duration: 0.16,
            ease: 'easeIn' as const,
            when: 'afterChildren' as const,
        },
    },
}

const modalVariants = {
    hidden: { y: 120, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            type: 'spring' as const,
            stiffness: 360,
            damping: 28,
            mass: 0.9,
            delay: 0.08,
        },
    },
    exit: {
        y: 120,
        opacity: 0,
        transition: {
            duration: 0.14,
            ease: 'easeIn' as const,
        },
    },
}

type ToggleRowConfig = {
    id: string
    label: string
    activeThumbIcon: ReactNode
    activeTrackIcon: ReactNode
    inactiveThumbIcon: ReactNode
    inactiveTrackIcon: ReactNode
    active?: boolean
    onChange?: (active: boolean) => void
}

type FooterLinkConfig = {
    id: string
    label: string
    url: string
}

type FullscreenDocument = Document & {
    webkitFullscreenElement?: Element | null
    webkitExitFullscreen?: () => Promise<void> | void
}

type FullscreenElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void
}

const footerLinks: FooterLinkConfig[] = [
    { id: 'imprint', label: 'Impressum', url: 'https://www.neueleben.de/impressum.html' },
    { id: 'privacy', label: 'Datenschutz', url: 'https://www.neueleben.de/datenschutz.html' },
]

function withSettingsToggleSound(onChange?: (active: boolean) => void) {
    return (active: boolean) => {
        playButtonSecondarySound()
        onChange?.(active)
    }
}

const SettingsScreen = observer(function SettingsScreen() {
    const [isAndroid, setIsAndroid] = useState(false)
    const [isFullscreenActive, setIsFullscreenActive] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        setIsAndroid(/Android/i.test(window.navigator.userAgent))
    }, [])

    useEffect(() => {
        if (typeof document === 'undefined') return

        const fullscreenDocument = document as FullscreenDocument

        const syncFullscreenState = () => {
            setIsFullscreenActive(
                Boolean(
                    document.fullscreenElement ??
                    fullscreenDocument.webkitFullscreenElement,
                ),
            )
        }

        syncFullscreenState()

        document.addEventListener('fullscreenchange', syncFullscreenState)
        document.addEventListener('webkitfullscreenchange', syncFullscreenState as EventListener)

        return () => {
            document.removeEventListener('fullscreenchange', syncFullscreenState)
            document.removeEventListener('webkitfullscreenchange', syncFullscreenState as EventListener)
        }
    }, [])

    const handleVibrationChange = useCallback((active: boolean) => {
        store.setVibrationEnabled(active)

        if (active && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            navigator.vibrate(40)
        }
    }, [])

    const handleShare = useCallback(() => {
        void shareGameLink(appendRefIdToUrl(getGameShareUrl()), 'settings')
    }, [])

    const handleFullscreenChange = useCallback(async (active: boolean) => {
        if (typeof document === 'undefined') return

        const fullscreenDocument = document as FullscreenDocument
        const rootElement = document.documentElement as FullscreenElement

        try {
            if (active) {
                if (document.fullscreenElement || fullscreenDocument.webkitFullscreenElement) {
                    setIsFullscreenActive(true)
                    return
                }

                if (rootElement.requestFullscreen) {
                    await rootElement.requestFullscreen()
                } else {
                    await rootElement.webkitRequestFullscreen?.()
                }
            } else if (document.fullscreenElement || fullscreenDocument.webkitFullscreenElement) {
                if (document.exitFullscreen) {
                    await document.exitFullscreen()
                } else {
                    await fullscreenDocument.webkitExitFullscreen?.()
                }
            }
        } catch {
            setIsFullscreenActive(
                Boolean(
                    document.fullscreenElement ??
                    fullscreenDocument.webkitFullscreenElement,
                ),
            )
        }
    }, [])

    const toggleRows: ToggleRowConfig[] = useMemo(() => [
        {
            id: 'sound',
            label: 'Sound',
            activeThumbIcon: <IconActiveSoundOn />,
            activeTrackIcon: <IconInactiveSoundOff />,
            inactiveThumbIcon: <IconActiveSoundOff />,
            inactiveTrackIcon: <IconInactiveSoundOn />,
            active: store.soundEnabled,
            onChange: store.setSoundEnabled,
        },
        {
            id: 'tutorial',
            label: 'Tutorial',
            activeThumbIcon: <IconActiveTutorialOn />,
            activeTrackIcon: <IconInactiveTutorialOff />,
            inactiveThumbIcon: <IconActiveTutorialOff />,
            inactiveTrackIcon: <IconInactiveTutorialOn />,
            active: store.tutorialEnabled,
            onChange: store.setTutorialEnabled,
        },
        {
            id: 'vibration',
            label: 'Vibration',
            activeThumbIcon: <IconActiveVibrationOn />,
            activeTrackIcon: <IconInactiveVibrationOff />,
            inactiveThumbIcon: <IconActiveVibrationOff />,
            inactiveTrackIcon: <IconInactiveVibrationOn />,
            active: store.vibrationEnabled,
            onChange: handleVibrationChange,
        },
        {
            id: 'fullscreen',
            label: 'Vollbild',
            activeThumbIcon: <IconActiveFullscreenOn />,
            activeTrackIcon: <IconInactiveFullscreenOff />,
            inactiveThumbIcon: <IconActiveFullscreenOff />,
            inactiveTrackIcon: <IconInactiveFullscreenOn />,
            active: isFullscreenActive,
            onChange: handleFullscreenChange,
        },
    ].filter((row) => {
        if ((row.id === 'vibration' || row.id === 'fullscreen') && !isAndroid) {
            return false
        }

        return true
    }), [
        handleFullscreenChange,
        handleVibrationChange,
        isAndroid,
        isFullscreenActive,
        store.soundEnabled,
        store.tutorialEnabled,
        store.vibrationEnabled,
    ])

    return (
        <motion.div
            className='z-90 absolute inset-0 flex justify-center items-center bg-black/55 backdrop-blur-sm p-8 pointer-events-auto'
            variants={backdropVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
        >
            <motion.div
                className='relative flex justify-center items-center'
                variants={modalVariants}
            >
                <TextHeadline fontSize={48} leading={1} tracking='-2%' className='-top-12 z-30 absolute' colorPreset='red'>
                    Optionen
                </TextHeadline>
                <div
                    className='flex flex-col gap-4 px-4 py-8 rounded-4xl w-full min-w-62 h-full settings-background'
                    style={{
                        backgroundColor: '#80A8C7',
                        borderColor: '#5486AC',
                        filter: 'drop-shadow(0 2px 0 #5486AC)',
                        border: '1px solid #5486AC',
                    }}
                >
                    <div className='flex flex-col gap-1 w-full'>
                        {toggleRows.map((row) => (
                            <div
                                key={row.id}
                                className={clsx(
                                    'flex justify-between items-center bg-white-gradient py-4.5 pr-2.5 pl-3.5 min-w-54 toggle-container',
                                    toggleRows.length === 1 && 'rounded-2xl',
                                    toggleRows.length > 1 && toggleRows[0]?.id === row.id && 'rounded-t-2xl rounded-b-sm',
                                    toggleRows.length > 1 && toggleRows[toggleRows.length - 1]?.id === row.id && 'rounded-t-sm rounded-b-2xl',
                                    toggleRows.length > 2 && toggleRows[0]?.id !== row.id && toggleRows[toggleRows.length - 1]?.id !== row.id && 'rounded-sm',
                                )}
                            >
                                <p className='text-white-stroke-even-darker font-grandstander font-black text-[16px] italic uppercase leading-[100%] tracking-[2%]'>
                                    {row.label}
                                </p>
                                <Toggle
                                    activeThumbIcon={row.activeThumbIcon}
                                    activeTrackIcon={row.activeTrackIcon}
                                    inactiveThumbIcon={row.inactiveThumbIcon}
                                    inactiveTrackIcon={row.inactiveTrackIcon}
                                    active={row.active}
                                    onChange={withSettingsToggleSound(row.onChange)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className='flex flex-col gap-4 w-full'>
                        <Button type='button' icon={<IconOutlink fill='#FFFFFF' width='24' height='24' />} color='blue' onClick={handleShare} size='large'>
                            Spiel teilen
                        </Button>
                        <div className='flex justify-around gap-4 px-1 w-full'>
                            {footerLinks.map((link) => (
                                <div
                                    key={link.id}
                                    className='flex justify-center items-center gap-1 text-white-stroke-even-darker font-nunito font-bold text-[12px] leading-[100%] tracking-[-4%]'
                                >
                                    <a href={link.url} target='_blank'>{link.label}</a>
                                    <IconOutlink />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className='-bottom-5.5 absolute'>
                    <ButtonIcon onClick={store.closeSettings} icon={<IconClose />} color='red' variant='standalone' popupCloseSound />
                </div>
            </motion.div>
        </motion.div>
    )
})

export default SettingsScreen

